import GeoJSON from "ol/format/GeoJSON";

import XYZ from "ol/source/XYZ.js";
import TileLayer from "ol/layer/Tile.js";

import {
    Vector as VectorSource,
    ImageArcGISRest,
    TileJSON,
    OSM,
    BingMaps,
} from "ol/source";
import { Image as ImageLayer, Tile } from "ol/layer";

import produce from "immer";
import VectorLayer from "ol/layer/Vector.js";
import booleanIntersects from "@turf/boolean-intersects";
import LinearRing from "ol/geom/LinearRing.js";
import {
    LineString,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
    Point,
    Polygon,
} from "ol/geom.js";
import OL3Parser from "jsts/org/locationtech/jts/io/OL3Parser";
import { BufferOp } from "jsts/org/locationtech/jts/operation/buffer";
import { Feature } from "ol";
import Stroke from "ol/style/Stroke.js";
import Style from "ol/style/Style.js";
import CircleStyle from "ol/style/Circle.js";
import Fill from "ol/style/Fill.js";
import { Snap } from "ol/interaction";
import { makeArray } from "../../permissionUtils";

export const toggleVisibility = (map, id, newVis) => {
    let layer = getMapLayerById(map, id);
    if (layer) {
        layer.setVisible(newVis);
    }
    //check if layer has labels and update visible
    layer = getMapLayerById(map, id + "_label");
    if (layer) {
        layer.setVisible(newVis);
    }
};

export const changeOpacity = (map, id, opacity) => {
    getMapLayerById(map, id).setOpacity(opacity);
    //check if layer has labels and update Opacity
    let layer = getMapLayerById(map, id + "_label");
    if (layer) {
        layer.setOpacity(opacity);
    }
};

export const getMapLayerById = (map, id) => {
    return map
        .getLayers()
        .getArray()
        .find((layer) => layer.values_.Id === id);
};

export const getMapInteractionById = (map, id) => {
    return map
        .getInteractions()
        .getArray()
        .find((layer) => layer.values_.Id === id);
};

export const getFeatureWithId = (map, vectorId, featureId) => {
    const feature = getMapLayerById(map, vectorId).getSource().getFeatureById(featureId);
    return feature;
};

export const findMatchingStateLayer = (arrayOfObjects, id) => {
    const matchingLayer = arrayOfObjects.find((object) => object.Id === id);
    return matchingLayer;
};

/**
 * Takes parameter from config file and creates a resolution to limit visibility of layer
 * @param {Object} map Map object
 * @param {Number} Scale Integer value from config file representing scale ratio (1: 6000)
 * @returns Number to set resolution or undefined if no zoom limiter is specified
 */
export const configScaleToOlRes = (map, Scale) => {
    if (!Scale) {
        return undefined;
    }
    const unitString = map.getView().getProjection().getUnits();
    // Get pixels per map unit (feet or meters)
    const mapUnits = ["feet", "foot"].some((u) => unitString.toLowerCase().includes(u))
        ? 1152
        : 3779.5296;
    const res = Scale / mapUnits;
    return res;
};

export const addCoordinateLayer = (map) => {
    const coordinateLayer = new VectorLayer({
        source: new VectorSource(),
        properties: { Id: "coordinateLayer" },
    });
    coordinateLayer.setZIndex(1000);
    map.addLayer(coordinateLayer);
};

export const addDrawingLayer = (map) => {
    const drawLayer = new VectorLayer({
        source: new VectorSource(),
        properties: { Id: "drawLayer" },
    });
    drawLayer.setZIndex(1000);
    map.addLayer(drawLayer);
};
export const addGPSLayer = (map) => {
    let gpsLayer = new VectorLayer({
        source: new VectorSource(),
        properties: { Id: "gpsLayer" },
    });
    gpsLayer.setZIndex(999);
    map.addLayer(gpsLayer);
    gpsLayer = new VectorLayer({
        source: new VectorSource(),
        properties: { Id: "gpsLayerTrack" },
    });
    gpsLayer.setZIndex(998);
    map.addLayer(gpsLayer);
};

export const addPlotCoordinateLayer = (map) => {
    const plotCoordinateLayer = new VectorLayer({
        source: new VectorSource(),
        properties: { Id: "plotCoordinateLayer" },
        style: plotCoordinatesStyle,
    });
    plotCoordinateLayer.setZIndex(1000);
    map.addLayer(plotCoordinateLayer);
};

export const addXYZLayer = (map, prop) => {
    const xyzTileLayer = new TileLayer({
        source: new XYZ({
            url: prop.ConnectionString,
            crossOrigin: "anonymous",
        }),
        properties: { Id: "XYZ_BaseMap" },
    });
    xyzTileLayer.setZIndex(prop.ZIndex);
    xyzTileLayer.setVisible(prop.Visible);
    map.addLayer(xyzTileLayer);
};

export const addOpenStreetMap_Layer = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -1 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -1;
        }
    }
    const osmLayer = new Tile({
        source: new OSM({
            crossOrigin: "anonymous",
        }),
        properties: { Id: "OpenStreetMap" },
    });
    osmLayer.setVisible(prop.Visible);
    osmLayer.setZIndex(prop.ZIndex);
    osmLayer.setOpacity(prop.Opacity ?? 1);
    osmLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    osmLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(osmLayer);
};

export const addMapTiler_Street_Layer = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -2 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -2;
        }
    }
    const mapTilerLayer = new Tile({
        source: new TileJSON({
            url: `https://api.maptiler.com/maps/streets-v2/tiles.json?key=${
                process.env.REACT_APP_MAPTILER_KEY ?? ""
            }`,
            tileSize: 512,
            crossOrigin: "anonymous",
        }),
        properties: { Id: "MapTiler_Steet" },
    });
    mapTilerLayer.setVisible(prop.Visible);
    mapTilerLayer.setZIndex(prop.ZIndex);
    mapTilerLayer.setOpacity(prop.Opacity ?? 1);
    mapTilerLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    mapTilerLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(mapTilerLayer);
};

export const addMapTiler_Basic_Layer = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -3 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -3;
        }
    }
    const mapTilerLayer = new Tile({
        source: new TileJSON({
            url: `https://api.maptiler.com/maps/basic-v2/tiles.json?key=${
                process.env.REACT_APP_MAPTILER_KEY ?? ""
            }`,
            tileSize: 512,
            crossOrigin: "anonymous",
        }),
        properties: { Id: "MapTiler_Basic" },
    });
    mapTilerLayer.setVisible(prop.Visible);
    mapTilerLayer.setZIndex(prop.ZIndex);
    mapTilerLayer.setOpacity(prop.Opacity ?? 1);
    mapTilerLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    mapTilerLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(mapTilerLayer);
};

export const addMapTiler_Satellite_Layer = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -4 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -4;
        }
    }
    const mapTilerLayer = new Tile({
        source: new TileJSON({
            url: `https://api.maptiler.com/tiles/satellite-v2/tiles.json?key=${
                process.env.REACT_APP_MAPTILER_KEY ?? ""
            }`,
            tileSize: 512,
            crossOrigin: "anonymous",
        }),
        properties: { Id: "MapTiler_Satellite" },
    });
    mapTilerLayer.setVisible(prop.Visible);
    mapTilerLayer.setZIndex(prop.ZIndex);
    mapTilerLayer.setOpacity(prop.Opacity ?? 1);
    mapTilerLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    mapTilerLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(mapTilerLayer);
};

export const addBingMaps_RoadOnDemand = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -5 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -5;
        }
    }
    const tileLayer = new Tile({
        source: new BingMaps({
            key: process.env.REACT_APP_BING_KEY,
            imagerySet: "RoadOnDemand",
            crossOrigin: "anonymous",
        }),
        properties: { Id: "BingMaps_RoadOnDemand" },
    });
    tileLayer.setVisible(prop.Visible);
    tileLayer.setZIndex(prop.ZIndex);
    tileLayer.setOpacity(prop.Opacity ?? 1);
    tileLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    tileLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(tileLayer);
};

export const addBingMaps_Aerial = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -6 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -6;
        }
    }
    const tileLayer = new Tile({
        source: new BingMaps({
            key: process.env.REACT_APP_BING_KEY,
            imagerySet: "Aerial",
            crossOrigin: "anonymous",
        }),
        properties: { Id: "BingMaps_Aerial" },
    });
    tileLayer.setVisible(prop.Visible);
    tileLayer.setZIndex(prop.ZIndex);
    tileLayer.setOpacity(prop.Opacity ?? 1);
    tileLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    tileLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(tileLayer);
};

export const addBingMaps_AerialWithLabels = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -7 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -7;
        }
    }
    const tileLayer = new Tile({
        source: new BingMaps({
            key: process.env.REACT_APP_BING_KEY,
            imagerySet: "AerialWithLabelsOnDemand",
            crossOrigin: "anonymous",
        }),
        properties: { Id: "BingMaps_AerialWithLabels" },
    });
    tileLayer.setVisible(prop.Visible);
    tileLayer.setZIndex(prop.ZIndex);
    tileLayer.setOpacity(prop.Opacity ?? 1);
    tileLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    tileLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(tileLayer);
};

export const addBingMaps_CanvasDark = (map, prop) => {
    if (typeof prop === "string") {
        prop = { Visible: false, ZIndex: -8 };
    } else {
        if (!prop.hasOwnProperty("Visible")) {
            prop["Visible"] = false;
        }
        if (!prop.hasOwnProperty("ZIndex")) {
            prop["ZIndex"] = -8;
        }
    }
    const tileLayer = new Tile({
        source: new BingMaps({
            key: process.env.REACT_APP_BING_KEY,
            imagerySet: "CanvasDark",
            crossOrigin: "anonymous",
        }),
        properties: { Id: "BingMaps_CanvasDark" },
    });
    tileLayer.setVisible(prop.Visible);
    tileLayer.setZIndex(prop.ZIndex);
    tileLayer.setOpacity(prop.Opacity ?? 1);
    tileLayer.setMaxResolution(configScaleToOlRes(map, prop.MaxZoom));
    tileLayer.setMinResolution(configScaleToOlRes(map, prop.MinZoom));
    map.addLayer(tileLayer);
};

/**
 * @typedef {object} ArcImageLayerProperties
 * @property {string} DisplayName - The display name of the layer.
 * @property {string} [Format="jpgpng"] - The format of the image layer.
 * @property {string} ConnectionString - The URL of the ArcGIS image service.
 * @property {boolean} Visible - Whether the layer should be visible or not.
 * @property {number} [Opacity=1] - The opacity of the layer.
 * @property {number} MaxZoom - The maximum zoom level at which the layer should be visible.
 * @property {number} MinZoom - The minimum zoom level at which the layer should be visible.
 */

export const addArcGISImage = (map, properties) => {
    /**
     * Adds an ArcGIS image layer to an OpenLayers map.
     *
     * @param {object} map - The OpenLayers map object to which the ArcGIS image layer will be added.
     * @param {ArcImageLayerProperties} properties - An object containing the properties of the ArcGIS image layer.
     */
    const baseMap = new ImageLayer({
        source: new ImageArcGISRest({
            ratio: 1,
            params: {
                TRANSPARENT: true,
                FORMAT: properties.Format ?? "jpgpng",
                PIXELTYPE: "f32",
            },
            url: properties.ConnectionString,
            crossOrigin: "Anonymous",
        }),
    });
    baseMap.setProperties({ Id: `${properties.DisplayName}` });
    baseMap.setVisible(properties.Visible);
    baseMap.setOpacity(properties.Opacity ?? 1);
    baseMap.setMaxResolution(configScaleToOlRes(map, properties.MaxZoom));
    baseMap.setMinResolution(configScaleToOlRes(map, properties.MinZoom));
    map.addLayer(baseMap);
};

// Find all group layers that are turned off
//   Id of each layer inside of a group layer layer with Visible: false
export const getHiddenGroupLayers = (layers) => {
    const notVisibleGroupLayers = [];
    layers.forEach((layer) => {
        if (layer.hasOwnProperty("SubLayers") && layer.Visible === false) {
            const subLayers = makeArray(layer.SubLayers);
            if (subLayers.hasOwnProperty("Sublayers")) {
                subLayers.forEach((subLayer) => {
                    notVisibleGroupLayers.push(...getHiddenGroupLayers(subLayer));
                });
            } else {
                subLayers.forEach((subLayer) => notVisibleGroupLayers.push(subLayer.Id));
            }
        }
    });
    return notVisibleGroupLayers;
};

// takes an OpenLayers feature and tests if it intersects an OpenLayers geometry
// returns a boolean
export const doesIntersect = (feature, geomToTestAgainst) => {
    const format = new GeoJSON();
    const featureGeom = format.writeFeatureObject(feature);
    const geomToTest = format.writeGeometryObject(geomToTestAgainst);
    return booleanIntersects(featureGeom, geomToTest);
};

// distanceToBuffer must be given in the units of the geometry
// which is derived from the units of the map projection
// takes olGeometry and returns olGeometry
export const bufferGeometry = (geometryToBuffer, bufferSource, distance) => {
    // make sure buffer draw layer is empty before adding new draw
    if (bufferSource) {
        bufferSource.clear();
    }
    const parser = new OL3Parser();
    parser.inject(
        Point,
        LineString,
        LinearRing,
        Polygon,
        MultiPoint,
        MultiLineString,
        MultiPolygon
    );
    const jstsGeom = parser.read(geometryToBuffer);
    const bufferedGeom = BufferOp.bufferOp(jstsGeom, distance);
    const bufferedGeometry = parser.write(bufferedGeom);
    return bufferedGeometry;
};

// util for quickly add a feature to the map
// for testing purposes
export const addFeatureToMap = (map, feature) => {
    const source = new VectorSource();
    const vector = new VectorLayer({
        source: source,
        style: highlightStyle,
    });
    source.addFeature(feature);
    map.addLayer(vector);
};

// give the source and add a geometry to it
export const addGeometryToSource = (source, geometry) => {
    source.addFeature(new Feature(geometry));
};

export const createVectorFromCoordinates = (coords, style = highlightStyle) => {
    const feature = new Feature({ geometry: new Polygon(coords) });
    const source = new VectorSource({ features: [feature] });
    const vector = new VectorLayer({
        source: source,
        style: style,
    });
    return vector;
};

/* 
Snapping
*/

export const toggleSnap = (map, layer, snappingBoolean) => {
    const snapId = `${layer.Id}-markupSnap`;

    if (snappingBoolean === true) {
        const source = getMapLayerById(map, layer.Id).getSource();
        const snap = new Snap({ source: source });
        snap.setProperties({ Id: `${snapId}` });
        map.addInteraction(snap);
    } else {
        const interaction = getMapInteractionById(map, snapId);
        map.removeInteraction(interaction);
    }
};

export const addSnapInteractions = (map, snapLayers) => {
    snapLayers.forEach((layer) => {
        if (!layer.Snappable) return;
        const source = getMapLayerById(map, layer.Id).getSource();
        const snap = new Snap({ source: source });
        snap.setProperties({ Id: `${layer.Id}-markupSnap` });
        // snap.setActive(layer.Snappable);
        map.addInteraction(snap);
    });
};

export const removeSnapInteractions = (map) => {
    const interactions = map.getInteractions().getArray();
    // cannot modify interactions array in place
    // thus creation of array
    const idsOfInteractionsToRemove = [];
    interactions.forEach((inter) => {
        if (inter.values_.Id?.includes("-markupSnap")) {
            idsOfInteractionsToRemove.push(inter.values_.Id);
        }
    });
    idsOfInteractionsToRemove.forEach((id) => {
        const interaction = interactions.find((inter) => inter.values_.Id === id);
        map.removeInteraction(interaction);
    });
};

export const updateVisibility = (snapLayers, visibleLayers) => {
    const updatedVisiblity = produce(snapLayers, (draft) => {
        draft.forEach((layer, i) => {
            const newVisible = visibleLayers.find(
                (visLayer) => visLayer.Id === layer.Id
            ).Visible;
            draft[i].Visible = newVisible;
        });
    });
    return updatedVisiblity;
};

const highlightStyle = new Style({
    zIndex: 10000000,
    stroke: new Stroke({
        color: "blue",
        width: 2,
    }),
});

const plotCoordinatesStyle = new Style({
    zIndex: 10000000,
    image: new CircleStyle({
        radius: 5,
        fill: new Fill({
            color: "red",
        }),
    }),
});
